import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import ThermalSalesReportIMIN from "./ThermalSalesReportIMIN";
import ThermalSalesReport from "./ThermalSalesReport";
import * as api from "../../api";

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    zIndex: 13000,
  },
  dialogPaper: {
    width: "32%",
    maxWidth: 420,
    maxHeight: "calc(100vh - 100px)",
    marginTop: theme.spacing(2),
    marginBottom: 25,
    borderRadius: 12,
  },
  dialogTitle: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h2": { fontSize: "0.9375rem", fontWeight: 600 },
  },
  dialogContent: {
    padding: theme.spacing(1, 1.5),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
    "& > *": { margin: 0 },
  },
  controlRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  datePicker: {
    minWidth: 140,
    "& .MuiInputBase-root": { fontSize: "0.8125rem" },
  },
  previewCard: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    flex: "1 1 auto",
    minHeight: 120,
    maxHeight: "58vh",
    overflowY: "auto",
    borderRadius: 8,
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
}));

const DSRDialog = ({
  open,
  onClose,
  date,
  handleDateChange,
  handleSubmit,
  showPreview,
  rows,
  user,
  classes: parentClasses,
}) => {
  const classes = useStyles();
  const [reportType, setReportType] = useState("consolidated");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (open && reportType === "employee-wise") {
      api.fetchEmployees().then((res) => setEmployees(res.data || [])).catch(() => setEmployees([]));
    }
  }, [open, reportType]);

  const isWindows = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes("windows");
  };

  const reportRows =
    reportType === "employee-wise" && selectedEmployeeId
      ? (rows || []).filter(
          (inv) =>
            inv.employeeId === selectedEmployeeId ||
            (inv.employeeName && inv.employeeName === (employees.find((e) => e._id === selectedEmployeeId)?.name || ""))
        )
      : rows || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6" style={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          Daily Sales Report
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.controls}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Box className={classes.controlRow}>
              <KeyboardDatePicker
                margin="dense"
                label="Select Date"
                format="dd/MM/yyyy"
                value={date}
                onChange={handleDateChange}
                className={classes.datePicker}
                inputVariant="outlined"
                size="small"
              />
              <Button variant="contained" color="primary" onClick={handleSubmit} size="small" style={{ minHeight: 40 }}>
                Search Report
              </Button>
            </Box>
          </MuiPickersUtilsProvider>

          <Box className={classes.controlRow} style={{ alignItems: "center" }}>
            <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>
              Report type
            </Typography>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={(_, v) => {
                if (v != null) {
                  setReportType(v);
                  if (v === "consolidated") setSelectedEmployeeId("");
                }
              }}
              size="small"
            >
              <ToggleButton value="consolidated">Consolidated</ToggleButton>
              <ToggleButton value="employee-wise">Employee-wise</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {reportType === "employee-wise" && (
            <FormControl variant="outlined" size="small" className={classes.datePicker} style={{ minWidth: 180 }}>
              <InputLabel id="dsr-employee-label">Select employee</InputLabel>
              <Select
                labelId="dsr-employee-label"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                label="Select employee"
              >
                <MenuItem value="">
                  <em>All employees</em>
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp._id} value={emp._id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        {showPreview && (
          <Card className={classes.previewCard}>
            {isWindows() ? (
              <ThermalSalesReport
                userProfile={user.userProfile}
                salesData={{
                  invoices: reportRows,
                  isPrintReport: true,
                }}
                selectedDate={date}
                reportType={reportType}
                selectedEmployeeId={reportType === "employee-wise" ? selectedEmployeeId : null}
                selectedEmployeeName={
                  reportType === "employee-wise" && selectedEmployeeId
                    ? employees.find((e) => e._id === selectedEmployeeId)?.name || null
                    : null
                }
              />
            ) : (
              <ThermalSalesReportIMIN
                userProfile={user.userProfile}
                salesData={{
                  invoices: reportRows,
                  isPrintReport: true,
                }}
                selectedDate={date}
                reportType={reportType}
                selectedEmployeeId={reportType === "employee-wise" ? selectedEmployeeId : null}
                selectedEmployeeName={
                  reportType === "employee-wise" && selectedEmployeeId
                    ? employees.find((e) => e._id === selectedEmployeeId)?.name || null
                    : null
                }
              />
            )}
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DSRDialog;
